"use client";

import { BlurFade } from "@/components/ui/blur-fade";
import { Typography } from "@/components/ui/typography";
import { useCommonTranslation } from "@/features/i18n/libs/useCommonTranslation";
import { RichTrans } from "@/features/i18n/ui/rich-trans";
import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState, useMemo } from "react";
import { useTrackWhenInView } from "@/hooks/useTrackWhenInView";
import { type I18nTFunctionBound } from "@/types/customTypes";

const isProduction = true;

// Animation timing constants
// const DROP_ANIMATION_DURATION_S = 0.2;
const DROP_ANIMATION_DURATION_S = isProduction ? 1.2 : 0.2;
const SPEED_INCREASE_FACTOR = 0.95; // Each word falls 5% faster than previous
const WAIT_REDUCTION_FACTOR = 0.95; // Each word waits 20% less for previous to finish

// Rotation constants
const MAX_ROTATION_DEGREES = 15;
const ROTATION_MULTIPLIER = 3.7; // For deterministic rotation variation

// Horizontal positioning constants
const MAX_HORIZONTAL_OFFSET_PX = 60;
const HORIZONTAL_OFFSET_MULTIPLIER = 2.3; // For deterministic horizontal variation

const getQuestions = (t: I18nTFunctionBound) => [
  t("landing:question_1"),
  t("landing:question_2"),
  t("landing:question_3"),
  t("landing:question_4"),
  t("landing:question_5"),
  t("landing:question_6"),
  t("landing:question_7"),
  t("landing:question_8"),
];

const COLORS = [
  { bg: "bg-[#FFDA74]", text: "text-[#BE9D45]" },
  { bg: "bg-[#EFCCE0]", text: "text-[#B98490]" },
  { bg: "bg-[#ADE5B4]", text: "text-[#15B529]" },
  { bg: "bg-[#E5DEFF]", text: "text-[#755199]" },
  { bg: "bg-[#D3E4FD]", text: "text-[#457699]" },
  { bg: "bg-[#FFECE6]", text: "text-[#994545]" },
];

function generateDeterministicRotation(index: number): number {
  if (index === 0) return 0;

  // Use sine function for deterministic but varied rotation
  const rotation = Math.sin(index * ROTATION_MULTIPLIER) * MAX_ROTATION_DEGREES;
  return rotation;
}

function generateHorizontalOffset(index: number): number {
  if (index === 0) return 0; // First badge stays centered

  // Use cosine function for deterministic horizontal variation
  const offset =
    Math.cos(index * HORIZONTAL_OFFSET_MULTIPLIER) * MAX_HORIZONTAL_OFFSET_PX;
  return offset;
}

function scroll(el: HTMLElement) {
  document.body.style.overflow = "hidden";

  el.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });

  setTimeout(() => {
    el.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    document.body.style.overflow = "";
  }, 500);
}

export function QuestionsSection() {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const { t } = useCommonTranslation();
  const inViewRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(inViewRef, { amount: 0.3 });
  const shouldScrollTopRef = useRef(isProduction);
  const hasScrolledFromAnchorRef = useRef(false);

  const questionsRef = useTrackWhenInView<HTMLDivElement>(
    "questions_section_visible",
  );

  const QUESTIONS = getQuestions(t);

  // Memoize expensive animation calculations
  const animationConfig = useMemo(
    () => ({
      dropDuration: DROP_ANIMATION_DURATION_S,
      speedIncrease: SPEED_INCREASE_FACTOR,
      waitReduction: WAIT_REDUCTION_FACTOR,
    }),
    [],
  );

  // Memoize question animations
  const questionAnimations = useMemo(
    () =>
      QUESTIONS.map((question, index) => ({
        question,
        color: COLORS[index % COLORS.length] ?? COLORS[0] ?? COLORS[0],
        rotation: generateDeterministicRotation(index),
        horizontalOffset: generateHorizontalOffset(index),
        duration:
          animationConfig.dropDuration *
          Math.pow(animationConfig.speedIncrease, index),
        bottom: `${index * (9 - index * 0.25)}%`,
        initialY: `calc(-50vh - (240px - ${index * (50 - index * 3)}px))`,
      })),
    [QUESTIONS, animationConfig.dropDuration, animationConfig.speedIncrease],
  );

  useEffect(() => {
    const checkIfFromAnchor = () => {
      const faqSection = document.querySelector("[data-faq-section]");
      if (faqSection) {
        const rect = faqSection.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          hasScrolledFromAnchorRef.current = true;
          shouldScrollTopRef.current = false;
        }
      }
    };

    checkIfFromAnchor();
    window.addEventListener("scroll", checkIfFromAnchor);

    return () => {
      window.removeEventListener("scroll", checkIfFromAnchor);
    };
  }, []);

  useEffect(() => {
    if (
      isInView &&
      shouldScrollTopRef.current &&
      questionsRef.current &&
      !hasScrolledFromAnchorRef.current
    ) {
      shouldScrollTopRef.current = false;
      scroll(questionsRef.current);
      setTimeout(() => {
        setCurrentIndex(0);
      }, 300);
    }
  }, [isInView, questionsRef]);

  useEffect(() => {
    if (currentIndex >= 0 && currentIndex < QUESTIONS.length - 1) {
      // Use memoized duration calculation
      const currentAnimation = questionAnimations[currentIndex];
      const currentWordDuration = (currentAnimation?.duration ?? 0) * 1000;

      // Calculate what percentage of the current word's duration to wait
      const waitPercentage = Math.pow(
        animationConfig.waitReduction,
        currentIndex,
      );

      // Wait for a percentage of the current word's duration before starting next word
      const waitTime = currentWordDuration * waitPercentage;

      const timer = setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
      }, waitTime);

      return () => clearTimeout(timer);
    } else if (currentIndex === QUESTIONS.length - 1) {
      // All bubbles have started dropping, wait for the last one to finish
      const lastAnimation = questionAnimations[currentIndex];
      const lastBubbleDuration = (lastAnimation?.duration ?? 0) * 1000;

      const timer = setTimeout(() => {
        setShowScrollUp(true);
      }, lastBubbleDuration);

      return () => clearTimeout(timer);
    }
  }, [
    currentIndex,
    questionAnimations,
    animationConfig.waitReduction,
    QUESTIONS.length,
  ]);

  return (
    <div
      ref={questionsRef}
      className="relative flex h-full flex-col overflow-hidden"
      style={{ minHeight: "100%" }}
    >
      <div ref={inViewRef} className="absolute top-1/2" />
      <motion.div
        initial={{ height: "100%" }}
        animate={{
          height: showScrollUp ? "50%" : "100%",
        }}
        transition={{
          duration: 1,
          ease: "easeInOut",
        }}
        className="flex w-full flex-col p-4 pb-10 will-change-transform"
        style={{
          backfaceVisibility: "hidden",
        }}
      >
        <div className="relative w-full flex-1">
          {questionAnimations.map((animation, index) => {
            const shouldShow = index <= currentIndex;

            return (
              <motion.div
                key={animation.question}
                initial={{
                  y: animation.initialY,
                  opacity: 0,
                  rotate: 0,
                  x: 0,
                }}
                animate={
                  shouldShow
                    ? {
                        y: 0,
                        opacity: 1,
                        rotate: animation.rotation,
                        x: animation.horizontalOffset,
                      }
                    : {}
                }
                transition={{
                  duration: animation.duration,
                  ease: "linear",
                }}
                className="absolute inset-x-0 flex max-w-full justify-center will-change-transform"
                style={{
                  backfaceVisibility: "hidden",
                  // transform: "translateZ(0)",
                  bottom: animation.bottom,
                }}
                layout={false}
              >
                <div
                  className={`${animation.color?.bg ?? "bg-gray-100"} ${animation.color?.text ?? "text-gray-600"} rounded-full px-6 py-3 shadow-lg will-change-transform`}
                  style={{
                    backfaceVisibility: "hidden",
                  }}
                >
                  <Typography className="text-sm">
                    {animation.question}
                  </Typography>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      <motion.div
        initial={{
          opacity: 0,
          height: "0%",
        }}
        animate={
          showScrollUp
            ? {
                opacity: 1,
                height: "50%",
              }
            : {}
        }
        transition={{
          duration: 1,
          ease: "easeInOut",
        }}
        className="flex w-full items-center justify-center will-change-transform"
        style={{
          backfaceVisibility: "hidden",
        }}
        layout={false}
      >
        <div className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
          <div className="flex flex-col items-center gap-6 text-center sm:gap-8">
            <BlurFade delay={0.2}>
              <Typography className="max-w-5xl text-2xl font-semibold leading-cozy text-gray-900 sm:text-3xl md:text-4xl lg:text-5xl xl:text-[40px]">
                {t("landing:questions_title")}
              </Typography>
            </BlurFade>
            <BlurFade delay={0.4}>
              <div className="max-w-3xl">
                <Typography className="text-sm font-medium leading-none text-gray-700 sm:text-base md:text-lg lg:text-xl xl:text-lg">
                  <RichTrans i18nKey="landing:questions_description" />
                </Typography>
              </div>
            </BlurFade>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
